import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Imports Tiptap
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
// Componente Toolbar
import TiptapToolbar from './TiptapToolbar';
// Selector de Imágenes de Galería
import SelectorImagenesGaleria from './SelectorImagenesGaleria';
// Importaciones de React DnD
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// Estilos
import '../../styles/admin/Forms.css'; 
import '../../styles/admin/TiptapToolbar.css'; // Importar estilos del editor/toolbar
import { FaTimesCircle } from 'react-icons/fa'; // Icono para borrar imagen

// Componente para un elemento arrastrable (imagen)
const DraggableImageItem = ({ index, url, moveImage, isDelete, onDelete, badge, info, disabled, title }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: 'IMAGE_ITEM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  });

  const [, dropRef] = useDrop({
    accept: 'IMAGE_ITEM',
    hover: (item, monitor) => {
      if (item.index === index) {
        return;
      }
      moveImage(item.index, index);
      item.index = index;
    },
  });

  return (
    <div 
      ref={(node) => dragRef(dropRef(node))} 
      className={`image-preview-item ${isDelete ? 'marked-for-deletion' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <img src={url} alt={`Imagen ${index + 1}`} />
      <button 
        type="button" 
        onClick={onDelete} 
        className="btn-remove-single-image"
        disabled={disabled}
        title={title}
      >
        &times;
      </button>
      {badge && <span className={badge.className}>{badge.text}</span>}
      {info && (
        <div className="image-tag">
          {info}
        </div>
      )}
    </div>
  );
};

function SubcategoriaForm({ onSubmit, initialData = null, onCancel }) {
  const [nombre, setNombre] = useState('');
  // const [descripcion, setDescripcion] = useState(''); // Ya no usamos estado directo para HTML
  const [orden, setOrden] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [instagram, setInstagram] = useState('');
  const [web, setWeb] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Estados para imágenes
  const [existingImages, setExistingImages] = useState([]); // URLs de imágenes existentes
  const [newImageFiles, setNewImageFiles] = useState([]); // Archivos nuevos a subir
  const [imagePreviews, setImagePreviews] = useState([]); // Previews de archivos nuevos
  const [imagesToDelete, setImagesToDelete] = useState([]); // URLs a borrar
  
  // Estado para imágenes de galería
  const [galeriaSelectedImages, setGaleriaSelectedImages] = useState([]);

  // Configuración del editor TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configura las extensiones base si es necesario
        // heading: { levels: [1, 2, 3], },
      }),
      Link.configure({
        openOnClick: false, // No abrir enlaces en el editor
        autolink: true,
      }),
      TextStyle, // Necesario para Color
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'], // Aplicar alineación a títulos y párrafos
      }),
      Underline,
    ],
    content: initialData?.descripcion || '', // Contenido inicial del editor
    editable: !isSubmitting,
    editorProps: {
      attributes: {
        class: 'prosemirror-editor', // Puedes usar esto para CSS si prefieres
      },
    },
    // Actualizar estado o realizar acciones cuando el contenido cambia (opcional)
    // onUpdate: ({ editor }) => {
    //   console.log(editor.getHTML());
    // },
  });

  // Efecto para cargar datos iniciales
  useEffect(() => {
    if (initialData) {
      setNombre(initialData.nombre || '');
      setOrden(initialData.orden !== undefined && initialData.orden !== null ? String(initialData.orden) : '');
      setWhatsapp(initialData.whatsapp || '');
      setInstagram(initialData.instagram || '');
      setWeb(initialData.web || '');
      setUbicacion(initialData.ubicacion || '');
      // Guardar URLs existentes
      setExistingImages(initialData.imagenes || []); 
      editor?.commands.setContent(initialData.descripcion || '');
      // Resetear estados de edición de imágenes
      setNewImageFiles([]);
      setImagePreviews([]);
      setImagesToDelete([]);
      setGaleriaSelectedImages([]);
    } else {
      // Resetear todo para el modo añadir
      setNombre('');
      setOrden('');
      setWhatsapp('');
      setInstagram('');
      setWeb('');
      setUbicacion('');
      setExistingImages([]);
      setNewImageFiles([]);
      setImagePreviews([]);
      setImagesToDelete([]);
      setGaleriaSelectedImages([]);
      editor?.commands.setContent('');
    }
  }, [initialData, editor]);

   // Efecto para controlar si el editor se puede editar
   useEffect(() => {
        editor?.setEditable(!isSubmitting);
   }, [isSubmitting, editor]);

  // Manejar selección múltiple de archivos
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNewImageFiles(files);
      // Generar previews
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setImagePreviews(newPreviews);
    } 
    // No hacer nada si el usuario cancela (mantiene los archivos ya seleccionados)
    // Limpiar el input para permitir volver a seleccionar los mismos archivos si es necesario
    e.target.value = null;
  };

  // Marcar una imagen existente para borrar
  const handleDeleteExistingImage = (imageUrl) => {
    setImagesToDelete(prev => [...prev, imageUrl]);
    setExistingImages(prev => prev.filter(url => url !== imageUrl));
  };

  // Quitar un archivo nuevo de la selección
  const handleRemoveNewImage = (index) => {
    // Usar splice para mantener el orden correcto al eliminar
    const newFiles = [...newImageFiles];
    newFiles.splice(index, 1);
    setNewImageFiles(newFiles);
    
    const newPreviews = [...imagePreviews];
    newPreviews.splice(index, 1);
    setImagePreviews(newPreviews);
  };
  
  // Manejar imágenes seleccionadas de la galería
  const handleGaleriaImagesSelected = (selectedImages) => {
    setGaleriaSelectedImages(selectedImages);
  };

  // Funciones para reordenar imágenes con drag & drop
  const moveExistingImage = (fromIndex, toIndex) => {
    const updatedImages = [...existingImages];
    const [movedItem] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedItem);
    setExistingImages(updatedImages);
  };

  const moveNewImage = (fromIndex, toIndex) => {
    // Mover archivo
    const updatedFiles = [...newImageFiles];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    setNewImageFiles(updatedFiles);
    
    // Mover preview
    const updatedPreviews = [...imagePreviews];
    const [movedPreview] = updatedPreviews.splice(fromIndex, 1);
    updatedPreviews.splice(toIndex, 0, movedPreview);
    setImagePreviews(updatedPreviews);
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    const updatedImages = [...galeriaSelectedImages];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setGaleriaSelectedImages(updatedImages);
  };

  // Limpiar recursos de previews al desmontar o cambiar previews
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editor || !nombre) {
      setError('El nombre es obligatorio.');
      return;
    }
    setIsSubmitting(true);
    setError('');

    const descripcionHtml = editor.getHTML();

    const subcategoriaData = {
      nombre,
      descripcion: descripcionHtml, 
      whatsapp,
      instagram,
      web,
      ubicacion,
      orden: orden === '' ? null : (isNaN(parseInt(orden)) ? null : parseInt(orden)),
      // No pasamos `imagenes` aquí, se manejará en el padre
    };

    try {
        // Extraer las URLs de las imágenes de la galería
        const galeriaUrls = galeriaSelectedImages.map(img => img.url);
        
        // Pasamos los datos, los archivos nuevos, las URLs a borrar y las URLs de galería
        // Importante: Pasar las existingImages en su orden actual (después del drag & drop)
        await onSubmit(
          subcategoriaData, 
          newImageFiles,       // Archivos nuevos ordenados
          imagesToDelete,      // URLs marcadas para borrar
          galeriaUrls,         // URLs de galería ordenadas
          existingImages       // URLs existentes en su orden actual después del drag & drop
        );
        
        // Limpiar previews y selección al éxito
        setImagePreviews([]); 
        setNewImageFiles([]);
        setImagesToDelete([]);
        setGaleriaSelectedImages([]);
    } catch (err) {
        console.error("Error submitting subcategoria form:", err);
        setError(err.message || 'Ocurrió un error al guardar la subcategoría.');
    } finally {
        setIsSubmitting(false);
    }
  };

   // Limpiar recursos del editor al desmontar
    useEffect(() => {
        return () => {
            editor?.destroy();
        };
    }, [editor]);

  return (
    <form onSubmit={handleSubmit} className="admin-form subcategoria-form">
       <h4>{initialData ? 'Editar Subcategoría' : 'Añadir Nueva Subcategoría'}</h4>
      {error && <p className="error-message">{error}</p>}
      
      <div className="form-group">
        <label htmlFor="sub-nombre">Nombre *</label>
        <input
          id="sub-nombre"
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sub-descripcion">Descripción</label>
        <div className="tiptap-editor-wrapper">
            <TiptapToolbar editor={editor} />
            <EditorContent editor={editor} />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="sub-orden">Orden (número)</label>
        <input
          id="sub-orden"
          type="number"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          placeholder="Ej: 1 (menor número aparece primero)"
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sub-whatsapp">WhatsApp</label>
        <input
          id="sub-whatsapp"
          type="text"
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          placeholder="Ej: +54 9 11 1234-5678"
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sub-instagram">Instagram</label>
        <input
          id="sub-instagram"
          type="text"
          value={instagram}
          onChange={(e) => setInstagram(e.target.value)}
          placeholder="Ej: @ejemplo"
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sub-web">Web</label>
        <input
          id="sub-web"
          type="text"
          value={web}
          onChange={(e) => setWeb(e.target.value)}
          placeholder="Ej: https://www.ejemplo.com"
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sub-ubicacion">Ubicación (URL de Google Maps)</label>
        <input
          id="sub-ubicacion"
          type="url"
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
          placeholder="Ej: https://maps.google.com/?q=..."
          disabled={isSubmitting}
        />
        <small className="form-helper">
          Para obtener el enlace: busca la ubicación en Google Maps, haz clic en "Compartir", 
          selecciona "Copiar enlace" y pega aquí la URL completa.
        </small>
      </div>

      <div className="form-group">
        <label>Imágenes</label>
        
        {/* Opciones de subida de imágenes */}
        <div className="upload-methods">
          <div className="upload-method">
            <h5>Subir nuevas imágenes</h5>
            <input
              id="sub-imagenes"
              type="file"
              accept="image/png, image/jpeg, image/webp"
              multiple // Permitir selección múltiple
              onChange={handleImageChange}
              disabled={isSubmitting}
            />
          </div>
          
          {/* Selector de imágenes de la galería */}
          <div className="upload-method">
            <h5>Seleccionar de la galería</h5>
            <SelectorImagenesGaleria 
              onImagesSelected={handleGaleriaImagesSelected}
              initialSelectedImages={[]}
              multiple={true}
            />
          </div>
        </div>
        
        <DndProvider backend={HTML5Backend}>
          {/* Previsualización Imágenes Existentes (con botón borrar) */} 
          {existingImages.length > 0 && (
            <div className="image-previews-container existing-previews">
              <h5>Imágenes actuales: <small>(Arrastra para reordenar)</small></h5>
              <div className="image-previews-grid">
                {existingImages.map((imageUrl, index) => (
                  <DraggableImageItem
                    key={`exist-${index}`}
                    index={index}
                    url={imageUrl}
                    moveImage={moveExistingImage}
                    onDelete={() => handleDeleteExistingImage(imageUrl)}
                    info={`#${index + 1}`}
                    disabled={isSubmitting}
                    title="Marcar para borrar"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Previsualización Imágenes Nuevas (con botón quitar) */} 
          {imagePreviews.length > 0 && (
            <div className="image-previews-container new-previews">
              <h5>Nuevas imágenes a subir: <small>(Arrastra para reordenar)</small></h5>
              <div className="image-previews-grid">
                {imagePreviews.map((previewUrl, index) => (
                  <DraggableImageItem
                    key={`new-${index}`}
                    index={index}
                    url={previewUrl}
                    moveImage={moveNewImage}
                    onDelete={() => handleRemoveNewImage(index)}
                    badge={{ className: "new-image-badge", text: "Nueva" }}
                    disabled={isSubmitting}
                    title="Quitar de la selección"
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Mostrar imágenes seleccionadas de la galería */}
          {galeriaSelectedImages.length > 0 && (
            <div className="image-previews-container gallery-previews">
              <h5>Imágenes seleccionadas de la galería: <small>(Arrastra para reordenar)</small></h5>
              <div className="gallery-images-grid">
                {galeriaSelectedImages.map((image, index) => (
                  <DraggableImageItem
                    key={`gallery-${index}`}
                    index={index}
                    url={image.url}
                    moveImage={moveGalleryImage}
                    onDelete={() => setGaleriaSelectedImages(prev => prev.filter((_, i) => i !== index))}
                    badge={{ className: "gallery-image-badge", text: "Galería" }}
                    info={image.nombre}
                    disabled={isSubmitting}
                    title="Quitar de selección"
                  />
                ))}
              </div>
            </div>
          )}
        </DndProvider>
      </div>

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting} className="submit-button">
          {isSubmitting ? 'Guardando...' : (initialData ? 'Actualizar Subcategoría' : 'Añadir Subcategoría')}
        </button>
        {onCancel && (
           <button type="button" onClick={onCancel} disabled={isSubmitting} className="cancel-button">
             Cancelar
           </button>
        )}
      </div>
    </form>
  );
}

SubcategoriaForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onCancel: PropTypes.func, 
};

export default SubcategoriaForm; 